import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence, useInView, useScroll, useTransform } from 'framer-motion';

const StickyOuter = styled.div`
  @media (max-width: 768px) {
    height: 280vh;
    position: relative;
  }
`;

const Section = styled.section`
  position: relative;
  height: 88vh;
  min-height: 580px;
  overflow: hidden;
  cursor: default;

  @media (max-width: 768px) {
    position: sticky;
    top: 0;
    height: 100svh;
    min-height: unset;
  }
`;

const RightBase = styled.div`
  position: absolute;
  inset: 0;
  background: #111111;
`;

const LeftBase = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: #0D0D0D;
`;

/* Línea divisoria — vertical en desktop, horizontal en móvil */
const DivLine = styled(motion.div)`
  position: absolute;
  z-index: 20;
  pointer-events: none;

  /* Desktop: línea vertical */
  top: 0;
  bottom: 0;
  width: 1px;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(244,121,32,0.5) 18%,
    rgba(244,121,32,0.9) 50%,
    rgba(244,121,32,0.5) 82%,
    transparent 100%
  );

  @media (max-width: 768px) {
    /* Móvil: línea horizontal */
    left: 0;
    right: 0;
    bottom: auto;
    width: auto;
    height: 1px;
    background: linear-gradient(
      to right,
      transparent 0%,
      rgba(244,121,32,0.5) 18%,
      rgba(244,121,32,0.9) 50%,
      rgba(244,121,32,0.5) 82%,
      transparent 100%
    );
  }
`;

const Half = styled.div`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.1rem;
  text-align: center;
  max-width: 420px;
  width: 38vw;
  z-index: 15;
  pointer-events: none;

  @media (max-width: 768px) {
    width: 84vw;
    max-width: none;
    gap: 0.75rem;
  }
`;

const Eyebrow = styled.div`
  font-size: 0.7rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #F47920;
  font-weight: 600;
`;

const BigTitle = styled.h2`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: clamp(3.2rem, 6vw, 6rem);
  font-weight: 900;
  text-transform: uppercase;
  line-height: 1;
  color: #fff;
  margin: 0;
  letter-spacing: -0.01em;
`;

const OLine = styled(motion.div)`
  height: 2px;
  background: #F47920;
  border-radius: 1px;
`;

const Body = styled(motion.p)`
  font-size: 1.1rem;
  color: #E2E2E2;
  line-height: 1.9;
  text-align: justify;
  text-align-last: center;
  margin-top: 0.75rem;
  font-weight: 400;
  letter-spacing: 0.01em;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.8;
    margin-top: 0.5rem;
  }
`;

const Hint = styled(motion.div)`
  font-size: 0.62rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #2E2E2E;
`;

const ScrollHint = styled(motion.div)`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.35rem;
    position: absolute;
    bottom: 1.5rem;
    left: 50%;
    transform: translateX(-50%);
    z-index: 30;
    pointer-events: none;

    span {
      font-size: 0.58rem;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: #383838;
    }
    svg {
      width: 16px;
      height: 16px;
      color: #383838;
      animation: sbounce 1.6s ease-in-out infinite;
    }
    @keyframes sbounce {
      0%, 100% { transform: translateY(0); }
      50%       { transform: translateY(4px); }
    }
  }
`;

/* ─────────────────────────────── */

const EASE = [0.22, 1, 0.36, 1];
const DUR  = 0.55;

export default function About() {
  const stickyRef = useRef(null);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-15%' });

  const [isMobile, setIsMobile] = useState(false);
  const [autoExpanded, setAutoExpanded] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [mouseSide, setMouseSide] = useState(null);
  const [mobilePosNum, setMobilePosNum] = useState(50);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Desktop: auto-expande Misión al entrar en viewport
  useEffect(() => {
    if (!inView || isMobile) return;
    const t = setTimeout(() => setAutoExpanded(true), 350);
    return () => clearTimeout(t);
  }, [inView, isMobile]);

  // Móvil: scroll del StickyOuter controla la posición
  const { scrollYProgress } = useScroll({
    target: stickyRef,
    offset: ['start start', 'end end'],
  });
  const mobileScrollPos = useTransform(
    scrollYProgress,
    [0, 0.3, 0.7, 1],
    [50, 65, 65, 35]
  );

  useEffect(() => {
    return mobileScrollPos.on('change', v => setMobilePosNum(v));
  }, [mobileScrollPos]);

  // Posición de la línea
  const desktopSide = hovering ? mouseSide : autoExpanded ? 'mision' : null;
  const pos = isMobile
    ? mobilePosNum
    : desktopSide === 'mision' ? 65 : desktopSide === 'vision' ? 35 : 50;

  const side = isMobile
    ? (mobilePosNum >= 60 ? 'mision' : mobilePosNum <= 40 ? 'vision' : null)
    : desktopSide;

  const showScrollHint = isMobile && mobilePosNum < 54;

  const handleMove = (e) => {
    if (isMobile) return;
    const { left, width } = e.currentTarget.getBoundingClientRect();
    setMouseSide((e.clientX - left) / width < 0.5 ? 'mision' : 'vision');
  };

  // Clips y posiciones según orientación
  const clipPath = isMobile
    ? `polygon(0% 0%, 100% 0%, 100% ${pos}%, 0% ${pos}%)`
    : `polygon(0% 0%, ${pos}% 0%, ${pos}% 100%, 0% 100%)`;

  const divLineAnimate = isMobile
    ? { top: `${pos}%`, left: 0 }
    : { left: `${pos}%` };

  // En móvil: anclar desde el top (no centrar verticalmente) para que
  // el título no suba cuando aparece el texto debajo
  const misionPos = isMobile
    ? { left: '50%', top: '14%', transform: 'translateX(-50%)' }
    : { left: '25%' };

  const visionPos = isMobile
    ? { left: '50%', top: '65%', transform: 'translateX(-50%)' }
    : { left: '75%' };

  return (
    <StickyOuter ref={stickyRef}>
      <Section
        ref={ref}
        onMouseMove={handleMove}
        onMouseEnter={() => !isMobile && setHovering(true)}
        onMouseLeave={() => { if (!isMobile) { setHovering(false); setMouseSide(null); } }}
      >
        <RightBase />

        <LeftBase
          animate={{ clipPath }}
          transition={{ duration: DUR, ease: EASE }}
        />

        <DivLine
          animate={divLineAnimate}
          transition={{ duration: DUR, ease: EASE }}
        />

        {/* ── MISIÓN ── */}
        <Half style={misionPos}>
          <Eyebrow>Nuestra</Eyebrow>
          <BigTitle>Misión</BigTitle>
          <AnimatePresence mode="wait">
            {side === 'mision' ? (
              <Body
                key="m-body"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 6 }}
                transition={{ duration: 0.38 }}
              >
                Transformamos el esfuerzo humano y la dedicación en productos de la
                más alta calidad. Cada corte lleva el valor y compromiso de una
                empresa en constante evolución, trabajando para superar expectativas
                y brindar lo mejor a sus clientes.
              </Body>
            ) : (
              <Hint
                key="m-hint"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                {isMobile ? '↓ scroll' : '← hover para leer'}
              </Hint>
            )}
          </AnimatePresence>
          <OLine
            animate={{ width: side === 'mision' ? 52 : 32 }}
            transition={{ duration: 0.35 }}
          />
        </Half>

        {/* ── VISIÓN ── */}
        <Half style={visionPos}>
          <Eyebrow>Nuestra</Eyebrow>
          <BigTitle>Visión</BigTitle>
          <AnimatePresence mode="wait">
            {side === 'vision' ? (
              <Body
                key="v-body"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 6 }}
                transition={{ duration: 0.38 }}
              >
                Queremos crecer contigo, ser tu mejor opción y destacar a nivel
                glocal sin perder nuestra esencia, calidad y el trato humano que
                compartimos con un equipo orgulloso de formar parte de este camino.
              </Body>
            ) : (
              <Hint
                key="v-hint"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                {isMobile ? 'scroll ↓' : 'hover para leer →'}
              </Hint>
            )}
          </AnimatePresence>
          <OLine
            animate={{ width: side === 'vision' ? 52 : 32 }}
            transition={{ duration: 0.35 }}
          />
        </Half>

        <ScrollHint
          animate={{ opacity: showScrollHint ? 0.8 : 0 }}
          transition={{ duration: 0.4 }}
        >
          <span>scroll</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </ScrollHint>

      </Section>
    </StickyOuter>
  );
}
